import React from 'react';
import { useTheme } from '@mui/material';
import { Icon } from '../Icon';
import { AbstractButton } from '../AbstractButton';
import { FlexProps } from '../Flex';
import { Badge } from '../Badge';

export interface TagProps extends FlexProps {
  /**
   * What happens when the "X" button is pressed. No "X" button is rendered, if this prop is not
   * specified
   * */
  onRemove?: () => void;
}

/** A chip is an entry in a combobox, but can be used anywhere */
export const Tag: React.FC<TagProps> = ({ children, onRemove, sx, ...rest }) => {
  const {
    palette: { componentTokens },
  } = useTheme();
  const handleRemove = (event: React.MouseEvent) => {
    // Stop propagation to prevent parent Select from opening/closing
    event.stopPropagation();
    event.preventDefault();
    onRemove?.();
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    // MUI Select uses onMouseDown to handle opening/closing
    // We need to stop propagation here too
    event.stopPropagation();
  };

  return (
    <Badge
      spacing={2}
      m={0}
      {...rest}
      variant="primary-solid"
      size="small"
      borderStyle="rectangle"
      sx={{
        position: 'relative',
        zIndex: 1,
        py: '1px',
        px: 1,
        ...sx,
      }}
    >
      {children}
      {onRemove && (
        <AbstractButton
          onClick={handleRemove}
          onMouseDown={handleMouseDown}
          ml={2}
          aria-label="Remove"
          sx={{
            position: 'relative',
            zIndex: 2,
            pointerEvents: 'auto',
            color: componentTokens?.badges.colors.solid.brand.assets.default,
          }}
        >
          <Icon size="x-small" type="close" />
        </AbstractButton>
      )}
    </Badge>
  );
};
